import React, {useState} from "react";
import {NavLink, Route, Routes} from "react-router-dom";
import MainButton from "../../commons/mainButton";
import Loader from "../../commons/loader";
import {GLOBAL_BORDER_RADIUS} from "../../../costants";
import MyNormalText from "../../commons/myNormalText";
import DatePicker from "react-datepicker";
import {httpsCallable} from "firebase/functions";
import {functions} from "../../../firebase";
import {fromMomentDateToYYYYMMDD, toMomentDate, toMomentDateFromYYYYMMDD} from "../../../global/dates";
import {BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, Title, Tooltip} from 'chart.js';
import {Bar, Line} from 'react-chartjs-2';
import * as XLSX from "xlsx";
import Modal from "react-modal";
import {FaExclamationTriangle} from 'react-icons/fa';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend);

// Bind modal to app element for accessibility
Modal.setAppElement('#root');

function AppUsageStats({rentUser, premiumPalette}) {
    const primaryColor = premiumPalette.primaryColor;
    const secondaryColor = premiumPalette.secondaryColor;
    const primaryColorFourthAlternative = premiumPalette.primaryColorFourthAlternative;

    const [loading, setLoading] = useState(false);
    const [usageData, setUsageData] = useState(null);
    const [sessionsData, setSessionsData] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [prevCursor, setPrevCursor] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sessionDetails, setSessionDetails] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [expandedErrors, setExpandedErrors] = useState([]);

    // Separate states for date ranges
    const [sessionsFromDate, setSessionsFromDate] = useState(null);
    const [sessionsToDate, setSessionsToDate] = useState(null);
    const [statsFromDate, setStatsFromDate] = useState(null);
    const [statsToDate, setStatsToDate] = useState(null);

    function handleCalculateUsage() {
        if (!statsFromDate || !statsToDate) {
            return;
        }

        setLoading(true);
        const rentSuperUserAdminAppUsageView = httpsCallable(functions, "rentSuperUserAdminAppUsageView");
        const request = {
            rentId: rentUser.rentId,
            startDate: statsFromDate,
            endDate: statsToDate,
        };
        rentSuperUserAdminAppUsageView(request)
            .then((response) => setUsageData(response.data))
            .catch(() => setLoading(false))
            .finally(() => setLoading(false));
    }

    function handleCalculateSessions(cursor = null, direction = 'next') {
        if (!sessionsFromDate || !sessionsToDate) {
            return;
        }

        setLoading(true);
        const rentSuperUserAdminAppSessionsView = httpsCallable(functions, "rentSuperUserAdminAppSessionsView");
        const request = {
            rentId: rentUser.rentId,
            startDate: sessionsFromDate,
            endDate: sessionsToDate,
            pageSize: 20,
            cursor,
            direction,
        };
        rentSuperUserAdminAppSessionsView(request)
            .then((response) => {
                setSessionsData(response.data.sessions);
                setNextCursor(response.data.nextCursor);
                setPrevCursor(response.data.prevCursor);
            })
            .catch(() => setLoading(false))
            .finally(() => setLoading(false));
    }

    async function handleRowClick(deviceId, timestamp) {
        setModalLoading(true);
        setIsModalOpen(true);
        setExpandedErrors([]);
        const rentSuperUserAdminAppSessionDetailView = httpsCallable(functions, "rentSuperUserAdminAppSessionDetailView");
        try {
            const date = new Date(timestamp).toISOString().split('T')[0];
            const response = await rentSuperUserAdminAppSessionDetailView({
                rentId: rentUser.rentId,
                deviceId,
                date,
            });
            setSessionDetails(response.data.sessionDetails);
            setExpandedErrors(new Array(response.data.sessionDetails.error?.length || 0).fill(false));
        } catch (error) {
            console.error('Error fetching session details:', error);
            setSessionDetails(null);
        } finally {
            setModalLoading(false);
        }
    }

    function handleCloseModal() {
        setIsModalOpen(false);
        setSessionDetails(null);
        setExpandedErrors([]);
    }

    function toggleErrorExpansion(index) {
        setExpandedErrors(prev => {
            const newExpanded = [...prev];
            newExpanded[index] = !newExpanded[index];
            return newExpanded;
        });
    }

    function handleExportSessionsToExcel() {
        if (!sessionsData || !sessionsFromDate || !sessionsToDate) {
            return;
        }

        const excelData = sessionsData.map(session => ({
            "Device ID": session.deviceId,
            "Email": session.email,
            "Platform": session.platform.charAt(0).toUpperCase() + session.platform.slice(1),
            "Pagine Visitate": session.numPages,
            "Tempo Totale (s)": session.totalTime,
            "Data": new Date(session.timestamp).toLocaleDateString('it-IT'),
            "Errore": session.hasError ? "Sì" : "No",
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sessioni");

        worksheet["!cols"] = [
            {wch: 30}, // Device ID
            {wch: 30}, // Email
            {wch: 15}, // Platform
            {wch: 15}, // Pagine Visitate
            {wch: 15}, // Tempo Totale
            {wch: 25}, // Data
            {wch: 10}, // Errore
        ];

        XLSX.writeFile(workbook, `Sessioni_App_${sessionsFromDate}_to_${sessionsToDate}.xlsx`);
    }

    function prepareDownloadsChartData() {
        if (!usageData || !usageData.downloadsPerOS) return null;

        const dates = Array.from(new Set(Object.values(usageData.downloadsPerOS).flatMap(os => Object.keys(os)))).sort();
        const platforms = Object.keys(usageData.downloadsPerOS);

        const datasets = platforms.map(platform => ({
            label: platform.charAt(0).toUpperCase() + platform.slice(1),
            data: dates.map(date => usageData.downloadsPerOS[platform][date] || 0),
            backgroundColor: platform === 'android' ? 'rgba(75, 192, 192, 0.6)' : 'rgba(153, 102, 255, 0.6)',
        }));

        return {
            labels: dates,
            datasets,
        };
    }

    function preparePageTimesChartData() {
        if (!usageData || !usageData.pageTimes) return null;

        const dates = Array.from(new Set(Object.values(usageData.pageTimes).flatMap(page => Object.keys(page)))).sort();
        const pages = Object.keys(usageData.pageTimes);

        const datasets = pages.map(page => ({
            label: page,
            data: dates.map(date => usageData.pageTimes[page][date] || 0),
            borderColor: getRandomColor(),
            backgroundColor: getRandomColor(0.2),
            fill: false,
        }));

        return {
            labels: dates,
            datasets,
        };
    }

    function getRandomColor(opacity = 1) {
        const r = Math.floor(Math.random() * 255);
        const g = Math.floor(Math.random() * 255);
        const b = Math.floor(Math.random() * 255);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    function handleExportToExcel() {
        if (!usageData || !statsFromDate || !statsToDate) {
            return;
        }

        const workbook = XLSX.utils.book_new();

        const downloadDates = Array.from(new Set(Object.values(usageData.downloadsPerOS).flatMap(os => Object.keys(os)))).sort();
        const downloadData = downloadDates.map(date => ({
            Data: date,
            Android: usageData.downloadsPerOS.android[date] || 0,
            iOS: usageData.downloadsPerOS.ios[date] || 0,
        }));
        const downloadWorksheet = XLSX.utils.json_to_sheet(downloadData);
        downloadWorksheet["!cols"] = [
            {wch: 15}, // Data
            {wch: 10}, // Android
            {wch: 10}, // iOS
        ];
        XLSX.utils.book_append_sheet(workbook, downloadWorksheet, "Download");

        const pageTimeDates = Array.from(new Set(Object.values(usageData.pageTimes).flatMap(page => Object.keys(page)))).sort();
        const pages = Object.keys(usageData.pageTimes);
        const pageTimeData = pageTimeDates.map(date => {
            const row = {Data: date};
            pages.forEach(page => {
                row[page] = usageData.pageTimes[page][date] || 0;
            });
            return row;
        });
        const pageTimeWorksheet = XLSX.utils.json_to_sheet(pageTimeData);
        pageTimeWorksheet["!cols"] = [{wch: 15}, ...pages.map(() => ({wch: 20}))];
        XLSX.utils.book_append_sheet(workbook, pageTimeWorksheet, "Tempi Pagine");

        XLSX.writeFile(workbook, `Statistiche_App_${statsFromDate}_to_${statsToDate}.xlsx`);
    }

    const downloadsChartData = prepareDownloadsChartData();
    const pageTimesChartData = preparePageTimesChartData();

    const SessionsView = () => (
        <div style={{display: "flex", flexDirection: "column", gap: 20}}>
            <div style={{...pageStyle.section, backgroundColor: primaryColor}}>
                <MyNormalText text={"Statistiche Sessioni dal - al"} bold={true} fontSize={18}/>
                <div style={{display: "flex", gap: 10, flexWrap: "wrap"}}>
                    <DatePicker
                        selected={sessionsFromDate ? toMomentDateFromYYYYMMDD(sessionsFromDate).toDate() : null}
                        onChange={(date) => setSessionsFromDate(fromMomentDateToYYYYMMDD(toMomentDate(date)))}
                        showMonthYearDropdown={false}
                        className={"custom-input"}
                        dateFormat={"dd/MM/yyyy"}
                        locale={"it"}
                        placeholderText={"Data inizio"}
                    />
                    <DatePicker
                        selected={sessionsToDate ? toMomentDateFromYYYYMMDD(sessionsToDate).toDate() : null}
                        onChange={(date) => setSessionsToDate(fromMomentDateToYYYYMMDD(toMomentDate(date)))}
                        showMonthYearDropdown={false}
                        className={"custom-input"}
                        dateFormat={"dd/MM/yyyy"}
                        locale={"it"}
                        placeholderText={"Data Fine"}
                    />
                    <div style={{width: 120}}>
                        <MainButton
                            text={"Calcola"}
                            action={() => handleCalculateSessions()}
                            backgroundColor={secondaryColor}
                        />
                    </div>
                </div>
            </div>
            <div style={{width: 100}}>
                <MainButton
                    text={"Excel"}
                    action={handleExportSessionsToExcel}
                    secondaryImage={require('../../../imgs/actions/excel.png')}
                    secondaryImageHeight={20}
                    secondaryImageWidth={20}
                    backgroundColor={secondaryColor}
                    disabled={!sessionsData || sessionsData.length === 0}
                />
            </div>
            <div style={{...pageStyle.section, backgroundColor: primaryColor}}>
                <MyNormalText
                    text={'Sessioni Utente'}
                    bold={true}
                    fontSize={18}
                />
                {sessionsData && sessionsData.length > 0 ? (
                    <>
                        <table style={tableStyle.table}>
                            <thead>
                            <tr>
                                <th style={tableStyle.th}>Device ID</th>
                                <th style={tableStyle.th}>Email</th>
                                <th style={tableStyle.th}>Platform</th>
                                <th style={tableStyle.th}>Pagine Visitate</th>
                                <th style={tableStyle.th}>Tempo Totale (s)</th>
                                <th style={tableStyle.th}>Data</th>
                                <th style={tableStyle.th}>Errore</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sessionsData.map(session => (
                                <tr
                                    key={`${session.deviceId}_${session.timestamp}`}
                                    onClick={() => handleRowClick(session.deviceId, session.timestamp)}
                                    style={{cursor: 'pointer'}}
                                >
                                    <td style={tableStyle.td}>{session.deviceId}</td>
                                    <td style={tableStyle.td}>{session.email}</td>
                                    <td style={tableStyle.td}>
                                        {session.platform.charAt(0).toUpperCase() + session.platform.slice(1)}
                                    </td>
                                    <td style={tableStyle.td}>{session.numPages}</td>
                                    <td style={tableStyle.td}>{session.totalTime}</td>
                                    <td style={tableStyle.td}>
                                        {new Date(session.timestamp).toLocaleDateString('it-IT')}
                                    </td>
                                    <td style={tableStyle.td}>
                                        {session.hasError ? (
                                            <FaExclamationTriangle color="red" size={20}/>
                                        ) : (
                                            'Nessuno'
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        <div style={{display: "flex", gap: 10, marginTop: 10}}>
                            <MainButton
                                text={"Indietro"}
                                action={() => handleCalculateSessions(prevCursor, 'prev')}
                                backgroundColor={secondaryColor}
                                disabled={!prevCursor}
                            />
                            <MainButton
                                text={"Avanti"}
                                action={() => handleCalculateSessions(nextCursor, 'next')}
                                backgroundColor={secondaryColor}
                                disabled={!nextCursor}
                            />
                        </div>
                    </>
                ) : (
                    <MyNormalText text={"Nessuna sessione trovata per il range selezionato"}/>
                )}
            </div>
            <Modal
                isOpen={isModalOpen}
                onRequestClose={handleCloseModal}
                style={{
                    content: {
                        top: '5%',
                        bottom: '5%',
                        left: '50%',
                        right: 'auto',
                        transform: 'translateX(-50%)',
                        backgroundColor: primaryColor,
                        borderRadius: GLOBAL_BORDER_RADIUS,
                        padding: 20,
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                }}
            >
                <div style={{display: 'flex', flexDirection: 'column', gap: 15}}>
                    {modalLoading ? (
                        <Loader/>
                    ) : sessionDetails ? (
                        <>
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                <MyNormalText text={"Dettagli Sessione Aggregata"} bold={true} fontSize={18}/>
                                <MyNormalText text={`Device ID: ${sessionDetails.deviceInfo.uniqueId}`}/>
                                <MyNormalText text={`User ID: ${sessionDetails.userId}`}/>
                                <MyNormalText text={`Email: ${sessionDetails.email}`}/>
                                <MyNormalText
                                    text={`Platform: ${sessionDetails.platform.charAt(0).toUpperCase() + sessionDetails.platform.slice(1)}`}/>
                                <MyNormalText text={`App Version: ${sessionDetails.appVersion}`}/>
                                <MyNormalText text={`Last App Version: ${sessionDetails.lastAppVersion}`}/>
                                <MyNormalText text={`First Launch: ${sessionDetails.isFirstLaunch ? 'Sì' : 'No'}`}/>
                                <MyNormalText
                                    text={`Data: ${new Date(sessionDetails.timestamp).toLocaleDateString('it-IT')}`}/>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                <MyNormalText text={"Device Info"} bold={true} fontSize={16}/>
                                <MyNormalText text={`Model: ${sessionDetails.deviceInfo.model || 'N/A'}`}/>
                                <MyNormalText text={`OS Version: ${sessionDetails.deviceInfo.osVersion || 'N/A'}`}/>
                                <MyNormalText
                                    text={`Manufacturer: ${sessionDetails.deviceInfo.manufacturer || 'N/A'}`}/>
                            </div>
                            {sessionDetails.error && sessionDetails.error.length > 0 ? (
                                <div style={{display: 'flex', flexDirection: 'column'}}>
                                    <MyNormalText text={"Dettagli Errori"} bold={true} fontSize={16}/>
                                    {sessionDetails.error.map((error, index) => (
                                        <div key={index} style={{marginBottom: 10}}>
                                            <MyNormalText text={`Errore ${index + 1}: ${error.message}`}/>
                                            <MyNormalText
                                                text={`Timestamp: ${new Date(error.errorTimestamp).toLocaleString('it-IT')}`}/>
                                            <pre style={{
                                                whiteSpace: 'pre-wrap',
                                                fontSize: '0.9em',
                                                backgroundColor: '#f5f5f5',
                                                padding: 10,
                                                borderRadius: 5,
                                                overflowWrap: 'break-word'
                                            }}>
                                                    {error.stack}
                                                </pre>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{display: 'flex', flexDirection: 'column'}}>
                                    <MyNormalText text={"Dettagli Errori"} bold={true} fontSize={16}/>
                                    <MyNormalText text={"Nessun errore"}/>
                                </div>
                            )}
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                <MyNormalText text={"Pagine Visitate"} bold={true} fontSize={16}/>
                                {sessionDetails.pageTimes && sessionDetails.pageTimes.length > 0 ? (
                                    <div>
                                        <table style={tableStyle.table}>
                                            <thead>
                                            <tr>
                                                <th style={tableStyle.th}>Pagina</th>
                                                <th style={tableStyle.th}>Tempo (s)</th>
                                                <th style={tableStyle.th}>Parametri</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {sessionDetails.pageTimes.map((page, index) => (
                                                <tr key={index}>
                                                    <td style={tableStyle.td}>{page.screen}</td>
                                                    <td style={tableStyle.td}>{page.timeSpent}</td>
                                                    <td style={tableStyle.td}>{JSON.stringify(page.params)}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <MyNormalText text={" - "}/>
                                )}
                            </div>
                            <MainButton
                                text={"Chiudi"}
                                action={handleCloseModal}
                                backgroundColor={secondaryColor}
                            />
                        </>
                    ) : (
                        <MyNormalText text={"Errore nel caricamento dei dettagli"}/>
                    )}
                </div>
            </Modal>
        </div>
    );

    const StatsView = () => (
        <div style={{display: "flex", flexDirection: "column", gap: 20}}>
            <div style={{...pageStyle.section, backgroundColor: primaryColor}}>
                <MyNormalText text={"Statistiche dal - al"} bold={true} fontSize={18}/>
                <div style={{display: "flex", gap: 10}}>
                    <DatePicker
                        selected={statsFromDate ? toMomentDateFromYYYYMMDD(statsFromDate).toDate() : null}
                        onChange={(date) => setStatsFromDate(fromMomentDateToYYYYMMDD(toMomentDate(date)))}
                        showMonthYearDropdown={false}
                        className={"custom-input"}
                        dateFormat={"dd/MM/yyyy"}
                        locale={"it"}
                        placeholderText={"Data inizio"}
                    />
                    <DatePicker
                        selected={statsToDate ? toMomentDateFromYYYYMMDD(statsToDate).toDate() : null}
                        onChange={(date) => setStatsToDate(fromMomentDateToYYYYMMDD(toMomentDate(date)))}
                        showMonthYearDropdown={false}
                        className={"custom-input"}
                        dateFormat={"dd/MM/yyyy"}
                        locale={"it"}
                        placeholderText={"Data Fine"}
                    />
                    <div style={{width: 120}}>
                        <MainButton
                            text={"Calcola"}
                            action={handleCalculateUsage}
                            backgroundColor={secondaryColor}
                        />
                    </div>
                </div>
            </div>
            <div style={{width: 100}}>
                <MainButton
                    text={"Excel"}
                    action={handleExportToExcel}
                    secondaryImage={require('../../../imgs/actions/excel.png')}
                    secondaryImageHeight={20}
                    secondaryImageWidth={20}
                    backgroundColor={secondaryColor}
                    disabled={!usageData || !statsFromDate || !statsToDate}
                />
            </div>
            <div style={{...pageStyle.section, backgroundColor: primaryColor}}>
                <MyNormalText text={"Download per Sistema Operativo"} bold={true} fontSize={18}/>
                {downloadsChartData ? (
                    <Bar
                        data={downloadsChartData}
                        options={{
                            responsive: true,
                            plugins: {
                                legend: {position: 'top'},
                                title: {display: true, text: 'Download per Giorno e OS'},
                            },
                            scales: {
                                x: {stacked: true},
                                y: {stacked: true},
                            },
                        }}
                    />
                ) : (
                    <MyNormalText text={"Nessun dato disponibile"}/>
                )}
            </div>
            <div style={{...pageStyle.section, backgroundColor: primaryColor}}>
                <MyNormalText text={"Tempo Speso su Ogni Pagina"} bold={true} fontSize={18}/>
                {pageTimesChartData ? (
                    <Line
                        data={pageTimesChartData}
                        options={{
                            responsive: true,
                            plugins: {
                                legend: {position: 'top'},
                                title: {display: true, text: 'Tempo Speso per Pagina per Giorno (in secondi)'},
                            },
                        }}
                    />
                ) : (
                    <MyNormalText text={"Nessun dato disponibile"}/>
                )}
            </div>
        </div>
    );

    return (
        <div style={pageStyle.container}>
            {loading ? <Loader absoluteFullPage={true}/> : <></>}
            <div style={{width: '100%', height: 1, backgroundColor: primaryColorFourthAlternative}}/>
            <div style={{display: "flex", gap: 10}}>
                <NavLink
                    to="sessions"
                    style={({isActive}) => ({
                        padding: 10,
                        borderRadius: 50,
                        backgroundColor: isActive ? secondaryColor : primaryColor,
                        color: isActive ? 'white' : 'black',
                        textDecoration: 'none',
                    })}
                >
                    Sessioni
                </NavLink>
                <NavLink
                    to="stats"
                    style={({isActive}) => ({
                        padding: 10,
                        borderRadius: 50,
                        backgroundColor: isActive ? secondaryColor : primaryColor,
                        color: isActive ? 'white' : 'black',
                        textDecoration: 'none',
                    })}
                >
                    Statistiche
                </NavLink>
            </div>
            <Routes>
                <Route path="/sessions" element={<SessionsView/>}/>
                <Route path="/stats" element={<StatsView/>}/>
            </Routes>
        </div>
    );
}

const pageStyle = {
    container: {
        display: "flex",
        flexDirection: "column",
        gap: 20,
    },
    section: {
        display: "flex",
        flexDirection: "column",
        padding: 10,
        gap: 5,
        borderRadius: GLOBAL_BORDER_RADIUS,
    },
};

const tableStyle = {
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: 10,
    },
    th: {
        border: '1px solid #ddd',
        padding: 8,
        backgroundColor: '#f2f2f2',
        textAlign: 'left',
    },
    td: {
        border: '1px solid #ddd',
        padding: 8,
        fontSize: 14
    },
};

export default AppUsageStats;
